/**
 * Profile Settings Tab
 *
 * Allows users to manage their profile information:
 * - Display name
 * - Avatar upload
 */

import { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useUpdateProfile } from '@/hooks/useUserSettings';
import AvatarUpload from './AvatarUpload';
import { asApiError } from '@/utils/error.util';

export const ProfileSettings = () => {
  const { user } = useAuth();
  const updateProfile = useUpdateProfile();

  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Sync display name from user (adjust state during render on change)
  const [syncedDisplayName, setSyncedDisplayName] = useState(user?.display_name);
  if (user && user.display_name !== syncedDisplayName) {
    setSyncedDisplayName(user.display_name);
    setDisplayName(user.display_name || '');
  }

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      await updateProfile.mutateAsync({
        display_name: displayName.trim(),
      });

      setSuccess('Профилът е обновен успешно');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(asApiError(err).response?.data?.message || 'Неуспешно обновяване на профила');
    }
  };

  const isSaving = updateProfile.isPending;

  return (
    <div className="space-y-6">
      {/* Avatar Upload */}
      <div>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-dark-text-primary mb-3">
          Профилна снимка
        </h3>
        <AvatarUpload />
      </div>

      {/* Name Fields */}
      <form onSubmit={handleProfileUpdate} className="space-y-4">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-dark-text-primary">
          Лична информация
        </h3>

        {/* Display Name */}
        <div>
          <label htmlFor="display-name" className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">
            Име за показване *
          </label>
          <input
            id="display-name"
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            className="input"
            placeholder="Как да ви виждат останалите"
            maxLength={100}
            required
          />
          <p className="text-xs text-gray-500 dark:text-dark-text-muted mt-1">
            Така се показва името ви в каналите и съобщенията
          </p>
        </div>

        <button
          type="submit"
          className="btn-primary"
          disabled={isSaving}
        >
          {isSaving ? 'Запазване...' : 'Обнови профила'}
        </button>
      </form>

      {/* Success Message */}
      {success && (
        <div className="text-sm text-green-600 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg px-3 py-2">
          {success}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="text-sm text-red-600 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-3 py-2">
          {error}
        </div>
      )}
    </div>
  );
};

export default ProfileSettings;
